import type { AgentAttachment } from '@/services/agent/service'
import type { CommunicationStrategyBriefType } from '@/types/communication-strategy-brief'

export type BriefSubjectType = 'person' | 'company' | 'product' | 'group'

export type BriefStrategistHandoff = {
    subjectType?: BriefSubjectType
    subjectId?: string
    subjectName?: string
    briefType?: CommunicationStrategyBriefType
    storyId?: string
    packStoryIds?: string
}

const SUBJECT_TYPES = new Set<BriefSubjectType>(['person', 'company', 'product', 'group'])

function parseSubjectType(value: string | null): BriefSubjectType | undefined {
    if (!value) return undefined
    const normalized = value.trim().toLowerCase() as BriefSubjectType
    return SUBJECT_TYPES.has(normalized) ? normalized : undefined
}

export function parseBriefStrategistHandoff(searchParams: URLSearchParams): BriefStrategistHandoff {
    const subjectType = parseSubjectType(searchParams.get('subjectType'))
    const subjectId = searchParams.get('subjectId')?.trim() || undefined
    const subjectName = searchParams.get('subjectName')?.trim() || undefined
    const briefType = (searchParams.get('briefType')?.trim() || undefined) as
        | CommunicationStrategyBriefType
        | undefined
    const storyId = searchParams.get('storyId')?.trim() || undefined
    const packStoryIds = searchParams.get('packStoryIds')?.trim() || undefined

    return {
        ...(subjectType && subjectId ? { subjectType, subjectId } : {}),
        ...(subjectName ? { subjectName } : {}),
        ...(briefType ? { briefType } : {}),
        ...(storyId ? { storyId } : {}),
        ...(packStoryIds ? { packStoryIds } : {}),
    }
}

export function buildBriefStrategistAgentUrl(
    params: BriefStrategistHandoff & { conversationId?: string | null; fresh?: boolean }
): string {
    const qs = new URLSearchParams({ mode: 'brief' })
    if (params.subjectType && params.subjectId) {
        qs.set('subjectType', params.subjectType)
        qs.set('subjectId', params.subjectId)
    }
    if (params.subjectName) qs.set('subjectName', params.subjectName)
    if (params.briefType) qs.set('briefType', params.briefType)
    if (params.storyId) qs.set('storyId', params.storyId)
    if (params.packStoryIds) qs.set('packStoryIds', params.packStoryIds)
    if (params.fresh !== false) qs.set('fresh', '1')
    if (params.conversationId) qs.set('c', params.conversationId)
    return `/dashboard/agent?${qs.toString()}`
}

export function formatHeftProfileChipLabel(attachment: AgentAttachment): string {
    if (attachment.kind !== 'heft_profile') return ''
    const name = attachment.name?.trim()
    const typeLabel = attachment.subjectType.charAt(0).toUpperCase() + attachment.subjectType.slice(1)
    if (name) return `${name} (${typeLabel})`
    const shortId = attachment.id.length > 8 ? `…${attachment.id.slice(-6)}` : attachment.id
    return shortId ? `${typeLabel} · ${shortId}` : `${typeLabel} profile`
}

export function handoffToHeftProfileAttachment(handoff: BriefStrategistHandoff): AgentAttachment | null {
    if (!handoff.subjectType || !handoff.subjectId) return null
    return {
        kind: 'heft_profile',
        id: handoff.subjectId,
        subjectType: handoff.subjectType,
        name: handoff.subjectName,
    }
}

export function heftProfileAttachmentLabel(attachment: AgentAttachment): string | null {
    if (attachment.kind !== 'heft_profile') return null
    return formatHeftProfileChipLabel(attachment)
}
